import ArticleLayout, { P, H2, UL, LI, Strong, Note } from '../../components/ArticleLayout.jsx';

export default function DeloadWeeks({ related }) {
  return (
    <ArticleLayout
      title="Deload weeks: what they are and why every 4 weeks"
      summary="A deload is a planned drop in training stress for one week, scheduled before fatigue becomes a problem. Skipping deloads is one of the most common reasons natural lifters stall — and one of the easiest to fix."
      readingMin={5}
      date="2026-05-22"
      related={related}
    >
      <P>
        If your program tells you to drop your weights by 10% and your sets by half every fourth week, that's a deload. It's not an injury accommodation, it's not a rest week, and it's not optional — it's a scheduled recovery block that lets you keep progressing for months instead of stalling and burning out.
      </P>

      <H2>The fatigue problem</H2>
      <P>
        Every hard training week produces two things: a fitness gain (you get stronger) and a fatigue cost (your nervous system, joints, and connective tissue accumulate stress). The fitness gain shows up immediately. The fatigue accumulates silently — you feel a little flatter, sleep a little worse, recover a little slower — until one week you hit a wall and can't add weight to anything.
      </P>
      <Note>
        At that point, you're not weak — you're fatigued. Pushing harder makes it worse. The fix is to back off before you crash, not after.
      </Note>

      <H2>How a deload works</H2>
      <P>
        On a deload week, you keep the same exercises and the same training days, but you reduce the stress in two ways:
      </P>
      <UL>
        <LI><Strong>Weight:</Strong> Drop the bar by ~10% from your last working set on each exercise.</LI>
        <LI><Strong>Volume:</Strong> Cut your sets roughly in half. If you normally do 4 working sets, do 2. If you do 3, do 2.</LI>
        <LI><Strong>Intensity:</Strong> Stop 3–4 reps short of failure. No grinder sets, no PR attempts.</LI>
      </UL>
      <P>
        You should leave every deload-week session feeling like you barely worked out. That's the point. The bar speeds up, your joints stop aching, your sleep gets better, and going into week 5 you'll hit weights that felt impossible at the end of week 4.
      </P>

      <H2>Why every 4 weeks</H2>
      <P>
        Most natural lifters can push hard for 3–4 weeks before fatigue catches up. Advanced lifters and people on aggressive cuts may need deloads every 3 weeks. Beginners can sometimes go 5–6 weeks. TrainForge defaults to every 4 weeks because it's the right answer for the largest group of users, but you can adjust the cadence in your program settings.
      </P>
      <P>The signs you should deload sooner than your scheduled week:</P>
      <UL>
        <LI>Bar speed drops on weights you handled fine last week.</LI>
        <LI>Sleep gets noticeably worse for 3+ nights.</LI>
        <LI>Resting heart rate jumps 5+ bpm above your normal morning number.</LI>
        <LI>Mood crashes — small things irritate you more than usual.</LI>
        <LI>Multiple joints ache constantly, not just the one you trained.</LI>
      </UL>

      <H2>Why deloads beat "just take a week off"</H2>
      <P>
        A full week off feels appealing when you're fried, but it's worse for two reasons. First, you lose neural sharpness — a week with zero lifting noticeably hurts your bar feel for the first 2–3 sessions back. Second, you lose the routine. Skipped weeks have a habit of becoming skipped months.
      </P>
      <P>
        A deload keeps the routine alive (same days, same exercises, same gym trip) while removing the stress. You finish the week recovered AND in groove, which is why progression jumps after a deload.
      </P>

      <H2>What deloads don't fix</H2>
      <P>
        If you stall again immediately after a deload, the problem isn't fatigue — it's one of:
      </P>
      <UL>
        <LI><Strong>Too few calories.</Strong> You can't strength-train hard in a steep deficit forever.</LI>
        <LI><Strong>Too little protein.</Strong> Under 0.8 g/lb is usually the culprit.</LI>
        <LI><Strong>Poor sleep.</Strong> Under 6.5 hours/night will cap any program.</LI>
        <LI><Strong>Wrong exercise selection.</Strong> If a lift never progresses across multiple programs, it might not be the right movement for your leverages.</LI>
      </UL>

      <H2>Should I deload my first month?</H2>
      <P>
        Yes — even brand-new lifters. The first month doesn't usually produce enough fatigue to need recovery, but deloading once teaches you the rhythm: hard-hard-hard-easy-hard-hard-hard-easy. Trying to learn deloading at month four when you're already fried is much harder.
      </P>
    </ArticleLayout>
  );
}
